import { AxiosError } from 'axios';
import { extractErrorMessage } from './errorUtils';

/**
 * Global reference to the error dialog show function
 * This will be set by the ErrorDialogProvider when it mounts
 */
let showBusinessErrorFn: ((message: string) => void) | null = null;

/**
 * Sets the function to show business error dialogs
 * Called by ErrorDialogProvider on mount
 */
export const setBusinessErrorHandler = (handler: (message: string) => void) => {
  showBusinessErrorFn = handler;
};

/**
 * Clears the business error handler
 * Called by ErrorDialogProvider on unmount
 */
export const clearBusinessErrorHandler = () => {
  showBusinessErrorFn = null;
};

/**
 * Handles 422 business logic errors by showing a modal dialog
 * @param error - The error object (typically an AxiosError)
 */
export const handleBusinessError = (error: unknown): void => {
  if (!showBusinessErrorFn) {
    console.warn('[businessErrorHandler] Error dialog handler not available. Error:', error);
    return;
  }

  const message = extractErrorMessage(error);
  showBusinessErrorFn(message);
};

/**
 * Checks if an error is a 422 business logic error
 * Works with both AxiosError and standardized errors that preserve status info
 */
export const isBusinessError = (error: unknown): error is AxiosError => {
  // Check if it's an AxiosError
  if (error instanceof AxiosError) {
    return error.response?.status === 422;
  }
  
  // Check if it's a standardized error that was originally an AxiosError
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const errorWithStatus = error as { response?: { status?: number } };
    return errorWithStatus.response?.status === 422;
  }
  
  return false;
};

/**
 * Handles 422 errors if the error is a business error, otherwise does nothing
 */
export const handleBusinessErrorIfApplicable = (error: unknown): boolean => {
  if (isBusinessError(error)) {
    handleBusinessError(error);
    return true;
  }
  return false;
};

